import { DEFAULT_FLAGS } from '@capturi/feature-flags'
import { List, ListItem, useToast } from '@capturi/ui-components'
import {
  Box,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import { FC, ReactElement, memo, useCallback } from 'react'

import {
  useSystemSettings,
  useUpdateSystemSettings,
} from '../../../../shared/useSystemSettings'
import { Title } from '../../../../utils'
import { useFeatureFlags, useUpdateFeatureFlag } from './api/useFeatureFlags'
import { useMapFeatureFlagsAndSystemSettings } from './featureFlagMapper'

type Props = { organizationUid: string }

type Flag = {
  key: string
  value: boolean
  description: string
}

const FeatureFlags: FC<Props> = ({ organizationUid }) => {
  const toast = useToast()

  const { data } = useFeatureFlags(organizationUid)
  const { mutate: updateFeatureFlag, isPending } =
    useUpdateFeatureFlag(organizationUid)

  const { data: systemSettings } = useSystemSettings(organizationUid)

  const { mutate: updateSystemSettings } =
    useUpdateSystemSettings(organizationUid)

  const featureFlagsAndSystemSettings = useMapFeatureFlagsAndSystemSettings(
    data ?? DEFAULT_FLAGS,
    systemSettings,
  )

  const handleToggleSystemSettingsFeatureFlag = useCallback(
    (key: string, value: boolean): void => {
      if (!systemSettings) return
      switch (key) {
        case 'deleteCustomer':
          updateSystemSettings({
            deletionStrategy: {
              ...systemSettings.deletionStrategy,
              deleteCustomer: value,
            },
          })
          break
        case 'obfuscateSpeech':
          updateSystemSettings({
            speech: {
              obfuscateSpeech: value,
            },
          })
          break
        case 'anonymizeAsrData':
          updateSystemSettings({
            deletionStrategy: {
              ...systemSettings.deletionStrategy,
              anonymizeAsrData: value,
            },
          })
          break
      }
    },

    [systemSettings, updateSystemSettings],
  )

  const handleToggleFeatureFlag = useCallback(
    (key: string, value: boolean): void => {
      updateFeatureFlag(
        { key, value },
        {
          onError: (error) => {
            toast({ status: 'error', title: error.message })
          },
        },
      )
    },
    [updateFeatureFlag, toast],
  )

  const renderSystemSettingsFlagRow = useCallback(
    ({
      key,
      item,
    }: {
      key: string
      item: { value: boolean | undefined; description: string }
    }): ReactElement => {
      return (
        <ListItem key={key} py={1}>
          <FormControl display="flex" alignItems="center">
            <Switch
              size="sm"
              color="primary"
              isChecked={item.value}
              onChange={() => {
                handleToggleSystemSettingsFeatureFlag(key, !item.value)
              }}
              mr={2}
              isDisabled={isPending}
            />
            <VStack align="flex-start" gap={0}>
              <FormLabel m={0}>{key}</FormLabel>
              <Text fontSize="11px" color="gray.600">
                {item.description}
              </Text>
            </VStack>
          </FormControl>
        </ListItem>
      )
    },
    [handleToggleSystemSettingsFeatureFlag, isPending],
  )

  const renderFlagRow = useCallback(
    ({ key, value, description }: Flag): ReactElement => {
      return (
        <ListItem key={key} py={1}>
          <FormControl display="flex" alignItems="center">
            <Switch
              size="sm"
              color="primary"
              id={key}
              isChecked={value}
              onChange={() => handleToggleFeatureFlag(key, !value)}
              mr={2}
              isDisabled={isPending}
            />
            <VStack align="flex-start" gap={0}>
              <FormLabel htmlFor={key} m={0}>
                {key}
              </FormLabel>
              <Text fontSize="11px" color="gray.600">
                {description}
              </Text>
            </VStack>
          </FormControl>
        </ListItem>
      )
    },
    [handleToggleFeatureFlag, isPending],
  )

  return (
    <Card>
      <CardBody>
        <Title>Feature Flags</Title>
        <List>
          {featureFlagsAndSystemSettings.map((section) => {
            return Object.entries(section).map(([sectionKey, sectionData]) => {
              return (
                <Box key={sectionKey} mb={4}>
                  <Text fontWeight="semibold" fontSize="lg">
                    {sectionData.groupName}
                  </Text>
                  <List pt={0}>
                    {sectionData.type === 'systemSettings'
                      ? Object.entries(sectionData.flags).map(
                          ([flagKey, flagItem]) =>
                            renderSystemSettingsFlagRow({
                              key: flagKey,
                              item: flagItem,
                            }),
                        )
                      : Object.entries(sectionData.flags).map(
                          ([flagKey, flagItem]) =>
                            renderFlagRow({
                              key: flagKey,
                              value: flagItem.value,
                              description: flagItem.description,
                            }),
                        )}
                  </List>
                </Box>
              )
            })
          })}
        </List>
      </CardBody>
    </Card>
  )
}

export default memo(FeatureFlags)
